import React, { useState, useEffect, useContext, useCallback } from "react";
import {
  Box,
  Typography,
  TextField,
  IconButton,
  CircularProgress,
  Avatar,
  useTheme,
  Fade,
  Tooltip,
} from "@mui/material";
import { keyframes } from "@mui/system";
import EditNoteIcon from "@mui/icons-material/EditNote";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import NoteAddIcon from "@mui/icons-material/NoteAdd";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import supabase from "../supabaseClient";
import UserContext from "../contexts/userContext";

// Subtle pulse animation for empty state
const gentlePulse = keyframes`
  0%, 100% { opacity: 0.4; transform: scale(1); }
  50% { opacity: 0.6; transform: scale(1.02); }
`;

// Format relative time
const formatRelativeTime = (dateString) => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return "just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  
  return date.toLocaleDateString("en-US", { 
    month: "short", 
    day: "numeric",
    year: date.getFullYear() !== now.getFullYear() ? "numeric" : undefined
  });
};

function RecipeNotes({ recipeId }) {
  const theme = useTheme();
  const { user } = useContext(UserContext);
  const isDark = theme.palette.mode === "dark";
  const accentPrimary = isDark ? "#BB86FC" : "#6366f1";
  const accentSecondary = isDark ? "#03DAC6" : "#8b5cf6";

  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editContent, setEditContent] = useState("");
  const [userNote, setUserNote] = useState(null);

  // Get user avatar URL
  const getUserAvatar = (userId) => {
    if (user && userId === user.id) {
      return user.user_metadata?.avatar_url;
    }
    return null;
  };

  // Get user display info
  const getUserInfo = (userId) => {
    if (user && userId === user.id) {
      const name = user.user_metadata?.full_name || user.email?.split("@")[0] || "you";
      return { name, isCurrentUser: true };
    }
    return { name: `member`, isCurrentUser: false };
  };

  // Fetch notes for this recipe
  const fetchNotes = useCallback(async () => {
    if (!recipeId) return;
    
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("recipe_notes")
        .select("id, content, user_id, created_at, updated_at")
        .eq("recipe_id", recipeId)
        .order("updated_at", { ascending: false });

      if (error) throw error;

      setNotes(data || []);
      
      if (user) {
        const myNote = data?.find((n) => n.user_id === user.id);
        setUserNote(myNote || null);
        if (myNote) {
          setEditContent(myNote.content);
        }
      }
    } catch (error) {
      console.error("Error fetching notes:", error);
    } finally {
      setLoading(false);
    }
  }, [recipeId, user]);

  useEffect(() => {
    fetchNotes();
  }, [fetchNotes]);

  const handleSave = async () => {
    if (!user || !editContent.trim()) return;

    setSaving(true);
    try {
      if (userNote) {
        const { error } = await supabase
          .from("recipe_notes")
          .update({ content: editContent.trim() })
          .eq("id", userNote.id);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from("recipe_notes")
          .insert({
            recipe_id: recipeId,
            user_id: user.id,
            content: editContent.trim(),
          });
        if (error) throw error;
      }

      await fetchNotes();
      setIsEditing(false);
    } catch (error) {
      console.error("Error saving note:", error);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!userNote) return;

    setSaving(true);
    try {
      const { error } = await supabase
        .from("recipe_notes")
        .delete()
        .eq("id", userNote.id);

      if (error) throw error;

      setUserNote(null);
      setEditContent("");
      await fetchNotes();
    } catch (error) {
      console.error("Error deleting note:", error);
    } finally {
      setSaving(false);
    }
  };

  const handleStartEdit = () => {
    setEditContent(userNote?.content || "");
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setEditContent(userNote?.content || "");
    setIsEditing(false);
  };

  const otherNotes = notes.filter((n) => n.user_id !== user?.id);
  const hasAnyNotes = notes.length > 0;

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          py: 6,
        }}
      >
        <CircularProgress size={20} sx={{ color: accentPrimary }} />
      </Box>
    );
  }

  // Note card component
  const NoteCard = ({ note, isOwn = false }) => {
    const userInfo = getUserInfo(note.user_id);
    const avatarUrl = getUserAvatar(note.user_id);
    const isUpdated = note.updated_at !== note.created_at;
    
    return (
      <Box
        sx={{
          position: "relative",
          p: 2,
          borderRadius: "14px",
          backgroundColor: isOwn
            ? isDark
              ? `${accentPrimary}08`
              : `${accentPrimary}05`
            : isDark
              ? "rgba(255,255,255,0.02)"
              : "rgba(0,0,0,0.015)",
          border: `1px solid ${
            isOwn
              ? isDark ? `${accentPrimary}20` : `${accentPrimary}15`
              : isDark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.04)"
          }`,
          transition: "all 0.2s ease",
          "&:hover": {
            backgroundColor: isOwn
              ? isDark
                ? `${accentPrimary}12`
                : `${accentPrimary}08`
              : isDark
                ? "rgba(255,255,255,0.035)"
                : "rgba(0,0,0,0.025)",
          },
        }}
      >
        {/* Header with avatar and actions */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 1.5,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
            <Avatar
              src={avatarUrl}
              sx={{
                width: 28,
                height: 28,
                fontSize: "0.7rem",
                fontWeight: 600,
                background: isOwn
                  ? `linear-gradient(135deg, ${accentPrimary}, ${accentSecondary})`
                  : isDark
                    ? "rgba(255,255,255,0.1)"
                    : "rgba(0,0,0,0.08)",
                color: isOwn
                  ? "#fff"
                  : theme.palette.text.secondary,
                border: isOwn
                  ? `2px solid ${isDark ? accentPrimary : accentSecondary}`
                  : "none",
              }}
            >
              {!avatarUrl && userInfo.name.slice(0, 1).toUpperCase()}
            </Avatar>
            <Box>
              <Typography
                sx={{
                  fontFamily: "'JetBrains Mono', monospace",
                  fontSize: "0.75rem",
                  fontWeight: 600,
                  color: isOwn ? accentPrimary : theme.palette.text.primary,
                  lineHeight: 1.2,
                }}
              >
                {userInfo.isCurrentUser ? "you" : userInfo.name}
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <AccessTimeIcon sx={{ fontSize: 10, color: theme.palette.text.secondary, opacity: 0.6 }} />
                <Typography
                  sx={{
                    fontFamily: "'JetBrains Mono', monospace",
                    fontSize: "0.6rem",
                    color: theme.palette.text.secondary,
                    opacity: 0.7,
                  }}
                >
                  {formatRelativeTime(note.updated_at)}
                  {isUpdated && " (edited)"}
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Edit button for own note */}
          {isOwn && !isEditing && (
            <Tooltip title="edit note" arrow placement="top">
              <IconButton
                size="small"
                onClick={handleStartEdit}
                sx={{
                  p: 0.75,
                  borderRadius: "8px",
                  color: accentPrimary,
                  backgroundColor: isDark ? `${accentPrimary}15` : `${accentPrimary}10`,
                  "&:hover": {
                    backgroundColor: isDark ? `${accentPrimary}25` : `${accentPrimary}18`,
                  },
                }}
              >
                <EditNoteIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>

        {/* Note content */}
        <Typography
          sx={{
            fontFamily: "'JetBrains Mono', monospace",
            fontSize: "0.85rem",
            lineHeight: 1.75,
            color: theme.palette.text.primary,
            whiteSpace: "pre-wrap",
            pl: 0.5,
          }}
        >
          {note.content}
        </Typography>
      </Box>
    );
  };

  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column", gap: 2 }}>
      {/* User's editing interface */}
      {user && isEditing && (
        <Fade in={isEditing}>
          <Box
            sx={{
              p: 2,
              borderRadius: "14px",
              background: isDark
                ? `linear-gradient(135deg, ${accentPrimary}10 0%, ${accentSecondary}08 100%)`
                : `linear-gradient(135deg, ${accentPrimary}08 0%, ${accentSecondary}05 100%)`,
              border: `1px solid ${isDark ? `${accentPrimary}25` : `${accentPrimary}20`}`,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: 2 }}>
              <Avatar
                src={user.user_metadata?.avatar_url}
                sx={{
                  width: 28,
                  height: 28,
                  fontSize: "0.7rem",
                  fontWeight: 600,
                  background: `linear-gradient(135deg, ${accentPrimary}, ${accentSecondary})`,
                  color: "#fff",
                  border: `2px solid ${accentPrimary}`,
                }}
              >
                {!user.user_metadata?.avatar_url && 
                  (user.user_metadata?.full_name || user.email || "Y").slice(0, 1).toUpperCase()}
              </Avatar>
              <Typography
                sx={{
                  fontFamily: "'JetBrains Mono', monospace",
                  fontSize: "0.75rem",
                  fontWeight: 600,
                  color: accentPrimary,
                }}
              >
                {userNote ? "editing your note" : "adding a note"}
              </Typography>
            </Box>

            <TextField
              multiline
              minRows={3}
              maxRows={10}
              fullWidth
              autoFocus
              value={editContent}
              onChange={(e) => setEditContent(e.target.value)}
              placeholder="jot down your thoughts, substitutions, tips..."
              sx={{
                mb: 2,
                "& .MuiOutlinedInput-root": {
                  fontFamily: "'JetBrains Mono', monospace",
                  fontSize: "0.85rem",
                  lineHeight: 1.7,
                  borderRadius: "10px",
                  backgroundColor: isDark
                    ? "rgba(0,0,0,0.2)"
                    : "rgba(255,255,255,0.9)",
                  "& fieldset": {
                    borderColor: "transparent",
                  },
                  "&:hover fieldset": {
                    borderColor: isDark ? `${accentPrimary}40` : `${accentPrimary}30`,
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: accentPrimary,
                    borderWidth: "1px",
                  },
                },
                "& .MuiOutlinedInput-input": {
                  "&::placeholder": {
                    fontSize: "0.8rem",
                    opacity: 0.4,
                    fontStyle: "italic",
                  },
                },
              }}
            />

            <Box sx={{ display: "flex", gap: 1, justifyContent: "space-between" }}>
              <Box>
                {userNote && (
                  <Tooltip title="delete note" arrow>
                    <IconButton
                      size="small"
                      onClick={handleDelete}
                      disabled={saving}
                      sx={{
                        p: 0.75,
                        borderRadius: "8px",
                        color: theme.palette.error.main,
                        "&:hover": {
                          backgroundColor: `${theme.palette.error.main}15`,
                        },
                      }}
                    >
                      <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
              <Box sx={{ display: "flex", gap: 1 }}>
                <IconButton
                  size="small"
                  onClick={handleCancelEdit}
                  disabled={saving}
                  sx={{
                    p: 0.75,
                    borderRadius: "8px",
                    color: theme.palette.text.secondary,
                    "&:hover": {
                      backgroundColor: isDark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.04)",
                    },
                  }}
                >
                  <CloseIcon sx={{ fontSize: 18 }} />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={handleSave}
                  disabled={saving || !editContent.trim()}
                  sx={{
                    p: 0.75,
                    borderRadius: "8px",
                    backgroundColor: accentPrimary,
                    color: isDark ? "#121212" : "#fff",
                    "&:hover": {
                      backgroundColor: accentPrimary,
                      filter: "brightness(1.1)",
                    },
                    "&:disabled": {
                      backgroundColor: isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.08)",
                      color: theme.palette.text.disabled,
                    },
                  }}
                >
                  {saving ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <CheckIcon sx={{ fontSize: 18 }} />
                  )}
                </IconButton>
              </Box>
            </Box>
          </Box>
        </Fade>
      )}

      {/* User's existing note (when not editing) */}
      {user && userNote && !isEditing && (
        <NoteCard note={userNote} isOwn />
      )}

      {/* Add note button (when user has no note and not editing) */}
      {user && !userNote && !isEditing && (
        <Box
          onClick={handleStartEdit}
          sx={{
            p: 3,
            borderRadius: "14px",
            border: `2px dashed ${isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.08)"}`,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 1,
            cursor: "pointer",
            transition: "all 0.25s ease",
            animation: !hasAnyNotes ? `${gentlePulse} 3s ease-in-out infinite` : "none",
            "&:hover": {
              borderColor: accentPrimary,
              backgroundColor: isDark ? `${accentPrimary}08` : `${accentPrimary}04`,
              transform: "translateY(-2px)",
              "& .add-icon": {
                transform: "scale(1.1) rotate(5deg)",
                color: accentPrimary,
              },
              "& .add-text": {
                color: accentPrimary,
              },
            },
          }}
        >
          <NoteAddIcon
            className="add-icon"
            sx={{
              fontSize: 28,
              color: theme.palette.text.secondary,
              opacity: 0.5,
              transition: "all 0.25s ease",
            }}
          />
          <Typography
            className="add-text"
            sx={{
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: "0.8rem",
              color: theme.palette.text.secondary,
              opacity: 0.6,
              transition: "all 0.25s ease",
            }}
          >
            add your note
          </Typography>
        </Box>
      )}

      {/* Household notes */}
      {otherNotes.length > 0 && (
        <Box>
          <Typography
            sx={{
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: "0.65rem",
              fontWeight: 600,
              color: theme.palette.text.secondary,
              textTransform: "uppercase",
              letterSpacing: "0.1em",
              mb: 1.5,
              pl: 0.5,
              opacity: 0.7,
            }}
          >
            from your household
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            {otherNotes.map((note) => (
              <NoteCard key={note.id} note={note} />
            ))}
          </Box>
        </Box>
      )}

      {/* Empty state for logged out users */}
      {!user && notes.length === 0 && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            py: 6,
            gap: 1.5,
          }}
        >
          <NoteAddIcon
            sx={{
              fontSize: 36,
              color: theme.palette.text.secondary,
              opacity: 0.25,
            }}
          />
          <Typography
            sx={{
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: "0.8rem",
              color: theme.palette.text.secondary,
              opacity: 0.5,
              textAlign: "center",
            }}
          >
            sign in to add notes
          </Typography>
        </Box>
      )}

      {/* Show household notes even when logged out */}
      {!user && notes.length > 0 && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          {notes.map((note) => (
            <NoteCard key={note.id} note={note} />
          ))}
        </Box>
      )}
    </Box>
  );
}

export default RecipeNotes;
